import { codeSlug, matchesCode, type CatalogResponse, type EnrollmentSummary } from "@allergy123/contracts";

/** Reading-order helpers over the catalog and the learner's enrollments. */

export function moduleOf(cat: CatalogResponse, code: string) {
  return cat.modules.find((m) => m.code === code);
}

export function sectionOf(cat: CatalogResponse, id: string) {
  return cat.sections.find((s) => s.id === id);
}

/** Find a class by its code or its URL slug (`LEAD 201-01` or `lead-201-01`). */
export function classOf(cat: CatalogResponse, code: string) {
  for (const m of cat.modules) {
    const k = m.classes.find((c) => matchesCode(c.code, code));
    if (k) return { module: m, cls: k };
  }
  return undefined;
}

export function enrollmentFor(enrollments: EnrollmentSummary[], moduleCode: string) {
  return enrollments.find((e) => e.moduleCode === moduleCode && e.status !== "withdrawn");
}

/** Continue only into a published, incomplete class in the enrolled release. */
export function nextAvailableClass(cat: CatalogResponse, enrollments: EnrollmentSummary[]) {
  const available = cat.modules.flatMap((module) => {
    const e = enrollmentFor(enrollments, module.code);
    if (!e || e.releaseVersion !== module.releaseVersion) return [];
    return module.classes.filter((cls) => cls.media.includes("video") && !isComplete(e, cls.code))
      .sort((a, b) => a.sequence - b.sequence)
      .map((cls) => ({ module, cls, e, progress: e.progress.find((p) => p.classCode === cls.code) }));
  });
  const touched = available.filter((c) => c.progress).sort((a, b) => b.progress!.lastSeenAt.localeCompare(a.progress!.lastSeenAt))[0];
  return touched ?? available[0];
}

export function isComplete(e: EnrollmentSummary | undefined, classCode: string) {
  return Boolean(e?.completions.some((c) => c.classCode === classCode));
}

export function positionOf(e: EnrollmentSummary | undefined, classCode: string) {
  return e?.progress.find((p) => p.classCode === classCode)?.positionSeconds ?? 0;
}

/** The learner-facing URL of a class: codes keep their space; URLs use the slug. */
export function classPath(code: string) {
  return `/training/classes/${codeSlug(code)}`;
}

export function modulePath(cat: CatalogResponse, moduleCode: string) {
  const m = moduleOf(cat, moduleCode);
  return m ? `/training/${m.sectionId}/${m.slug}` : "/training";
}

export function minutesLabel(estimated: number | null) {
  return estimated ? `${estimated} min` : "10–15 min";
}

export const stageLabel: Record<string, string> = {
  mapped: "Outline only",
  drafting: "In production",
  "functional-review": "In review",
  "pilot-approved": "Pilot",
  "use-approved": "Approved",
  superseded: "Superseded",
};

export function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
